import { queryKeys, useInvalidateQueries } from "@birdwatch/client";
import { isRealtimeChannelAllowed } from "@birdwatch/common";

import { useRealtimeRelay } from "@/features/shared/realtime-relay";

/**
 * How long after upload an attachment still waits for its analysis. Analysis lands
 * within minutes; an older attachment without a summary never got one (a pre-AI
 * upload, or analysis skipped or failed), and listening for it would only add a
 * channel to the tab's one shared stream, which the relay caps.
 */
const ANALYSIS_WAIT_MS = 60 * 60 * 1000;

/**
 * Refreshes the conversation threads when this attachment's AI analysis lands —
 * the summary is rendered from the attachment inside its message. Listens on the
 * attachment's own relay channel, so it hears the analysis of anyone's upload,
 * not only one this tab made.
 */
export function AttachmentAnalysisListener({
  attachmentId,
  createdAt,
}: {
  attachmentId: string;
  /** ISO timestamp of the upload. */
  createdAt: string;
}) {
  const invalidate = useInvalidateQueries();
  const isAwaitingAnalysis =
    Date.now() - new Date(createdAt).getTime() < ANALYSIS_WAIT_MS;

  useRealtimeRelay({
    // A channel the relay would refuse stops every hook sharing the tab's stream.
    channels: isRealtimeChannelAllowed(attachmentId) ? [attachmentId] : [],
    enabled: isAwaitingAnalysis,
    events: ["attachment.processed", "transcript.ready"],
    onData: (payload) => {
      // Read through `payload`, never destructured: the union narrows on `event`.
      if (
        payload.event === "attachment.processed" &&
        payload.data.aiAnalyzed !== true
      ) {
        return;
      }
      void invalidate(queryKeys.getChannelsMessagesByCid());
    },
  });

  return null;
}
